import { useState } from 'react';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { submitFeedback, type FeedbackRating } from '../lib/api';
import { cn } from '../lib/utils';

interface PlaygroundDayFeedbackProps {
  /** ISO YYYY-MM-DD — the date the agent assigned to this workout. */
  workoutDate: string;
  /** The exact day dict the agent emitted (frozen as workout_snapshot). */
  daySnapshot: Record<string, unknown>;
  /** The synthetic profile the playground ran against (frozen as user_profile_snapshot). */
  profileSnapshot: Record<string, unknown>;
}

/**
 * Thumbs+notes for a single playground-generated workout day.
 *
 * Self-contained — no Zustand store hookup. Each playground run is
 * transient, so we don't try to hydrate prior ratings (each run is a
 * fresh sample). Submitting fires POST /admin/validation/feedback with
 * scope='playground' and inline snapshots; user_id is intentionally
 * omitted because the playground runs against a synthetic profile that
 * has no row in `profiles`.
 *
 * Append-only by design: the partial unique index that dedupes day-scope
 * feedback only applies to scope='day'. Each playground submission
 * creates a fresh row, which is what we want for post-training data —
 * if the admin re-runs the playground and re-rates the same date, that's
 * a new sample, not an overwrite.
 */
export function PlaygroundDayFeedback({
  workoutDate,
  daySnapshot,
  profileSnapshot,
}: PlaygroundDayFeedbackProps) {
  const [draftRating, setDraftRating] = useState<FeedbackRating | null>(null);
  const [draftNotes, setDraftNotes] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Once a row has been submitted, we lock it visually so the admin
   *  doesn't accidentally double-save. They can still rate the same
   *  day again after resetting. */
  const [submittedRating, setSubmittedRating] = useState<FeedbackRating | null>(null);

  function pickRating(next: FeedbackRating) {
    setDraftRating(next);
    setExpanded(true);
  }

  async function handleSubmit() {
    if (!draftRating) return;
    setIsSubmitting(true);
    try {
      await submitFeedback({
        scope: 'playground',
        workout_date: workoutDate,
        rating: draftRating,
        notes: draftNotes.trim() || undefined,
        workout_snapshot: daySnapshot,
        user_profile_snapshot: profileSnapshot,
      });
      toast.success('Playground feedback saved');
      setSubmittedRating(draftRating);
      setExpanded(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save feedback';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleReset() {
    setDraftRating(null);
    setDraftNotes('');
    setExpanded(false);
    setSubmittedRating(null);
  }

  // Show pressed-state on the rating that was last submitted, so the
  // admin can see at a glance which days they've already rated this run.
  const activeRating = submittedRating ?? draftRating;

  return (
    <div className="px-3 py-2 border-t bg-muted/20">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => pickRating('up')}
            disabled={isSubmitting}
            title="Good workout"
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full border transition',
              activeRating === 'up'
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600',
            )}
          >
            <ThumbsUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => pickRating('down')}
            disabled={isSubmitting}
            title="Bad workout"
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full border transition',
              activeRating === 'down'
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600',
            )}
          >
            <ThumbsDown className="h-3 w-3" />
          </button>
          {submittedRating && !expanded && (
            <span className="text-[10px] text-muted-foreground">
              {submittedRating === 'up' ? '👍 saved' : '👎 saved'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {submittedRating && !expanded && (
            <button
              type="button"
              onClick={handleReset}
              className="text-[10px] text-muted-foreground hover:text-foreground"
            >
              Rate again
            </button>
          )}
          {(draftRating || submittedRating) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[10px] text-muted-foreground hover:text-foreground"
            >
              {expanded ? 'Hide' : 'Add notes'}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-2 space-y-2">
          <textarea
            rows={2}
            placeholder={
              draftRating === 'up'
                ? 'Why is this correct? (good pairing, right intensity, fits the user profile…)'
                : draftRating === 'down'
                  ? 'What is wrong? (wrong scheme, missing primary lift, mismatched intensity…)'
                  : 'Pick 👍 or 👎 first.'
            }
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            disabled={isSubmitting || !draftRating}
            className="w-full resize-none rounded-md border bg-background p-2 text-[11px] focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary disabled:bg-muted"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setExpanded(false);
                if (!submittedRating) setDraftRating(null);
                setDraftNotes('');
              }}
              disabled={isSubmitting}
              className="px-2 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!draftRating || isSubmitting}
              className={cn(
                'flex items-center gap-1 rounded-md px-2.5 py-0.5 text-[10px] font-medium transition',
                draftRating && !isSubmitting
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {isSubmitting && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
